/* v2 design tokens — authored fresh, NOT derived from app/static/app.css (v1's
   sheet dies at cutover). Custom properties are set on :root so they inherit
   through every shadow root; components never redeclare a raw colour.

   Installed into document.adoptedStyleSheets by installGlobalStyles(). */

/** Breakpoint used for the rail↔bottom-bar swap. Media queries can't read a
    custom property, so this constant is the single source of truth and the
    @media rules below reference it by value. Keep them in sync. */
export const BP_DESKTOP = 960;

const css = String.raw;

const TOKENS = css`
  :root {
    /* warm neutral ramp + marigold accent — the grain identity */
    --bg: #f4efe4;
    --surface: #fbf8f1;
    --surface-2: #ece5d6;
    --text: #241f18;
    --text-dim: #6d6355;
    --border: #ddd3c0;
    --accent: #c8811f;
    --accent-ink: #ffffff;
    --danger: #a33218;

    --space-1: 4px;
    --space-2: 8px;
    --space-3: 12px;
    --space-4: 16px;
    --space-5: 24px;
    --space-6: 32px;

    --radius-sm: 6px;
    --radius-md: 10px;
    --radius-lg: 16px;
    --radius-full: 999px;

    --font-display: 'Bricolage', system-ui, sans-serif;
    --font-text: 'Newsreader', Georgia, serif;
    --font-ui: 'Albert', system-ui, -apple-system, sans-serif;
    --font-mono: 'SplineMono', ui-monospace, monospace;

    /* shell geometry — g-rail and g-header read these, nothing hardcodes them.
       There is no collapsed width here on purpose: g-rail derives it from one
       row, so the two can't disagree. */
    --header-h: 56px;
    --rail-w: 208px;
    --rail-row-h: 38px;
    /* The tab bar's row of tabs, excluding the bottom safe-area inset it adds
       underneath — see g-tabbar. Icon-only since ADR 0009, so this is
       --touch-min exactly: the tabs sit directly above the system navigation
       bar, and anything shorter moves a thumb target towards a control that
       leaves the app. */
    --tabbar-h: 44px;

    /* Smallest thing a thumb should have to hit (ADR 0007 principle 8). Apple
       says 44, Android 48; 44 with real spacing around it beats 48 crammed. */
    --touch-min: 44px;

    /* Display cutouts, as ordinary spacing tokens rather than a style fragment:
       an inset is a *value*, and a value inherits through shadow roots while a
       rule does not. A component writes
       padding-bottom: calc(var(--space-2) + var(--safe-b))
       and needs to know nothing about env() or which edges exist.

       These resolve to 0px everywhere except an installed PWA on a notched
       device — and only because index.html sets viewport-fit=cover, without
       which env() is always 0. */
    --safe-t: env(safe-area-inset-top, 0px);
    --safe-r: env(safe-area-inset-right, 0px);
    --safe-b: env(safe-area-inset-bottom, 0px);
    --safe-l: env(safe-area-inset-left, 0px);

    /* Who owns the horizontal inset (ADR 0009 decision 3).

       Desktop: the CONTAINER does. A workspace pads itself by --gutter-outer and
       its rows sit inside that, which is the document idiom the desktop layout
       was designed as.

       Mobile: the ROW does. Containers go flush to the edge and each row insets
       its own content, so a row's background, divider and press state span the
       full width — the difference between a page of cards and a native list.

       A row's own padding is --row-pad-*, NOT --row-inset-*. The two differ by
       one step, and only on desktop: there a row still holds its text off the
       container's padding edge (36px from the screen), while on mobile the row
       inset IS the text inset (16px) and matches everything else at the screen
       edge — the chip band, a notice, a grid. Rows used to add that step
       themselves, at 23 identical call sites, which put every row 12px deeper
       than the band above it on a phone.

       --content-* is deliberately NOT derived from --row-pad-*: a block that is
       content in its own right sits at 24px on desktop, where a row's text sits
       at 36px. Those are two different questions and only agree on mobile.

       Only the two --gutter-* values flip; --row-inset-*, --row-pad-* and
       --content-* are derived, and var() re-resolves them when the media query
       changes their input.

       The max() is the landscape-cutout guard. Nothing else insets a full-bleed
       row, so without it a row's text runs under the notch when the phone is
       turned. It costs nothing where there is no cutout: --safe-l is 0px there,
       and 0 never wins a max(). Do NOT also pad #outlet by --safe-l — that would
       double-inset and pull the row backgrounds off the edge, which is the whole
       thing this buys. */
    --gutter-outer: var(--space-5);
    --gutter-inner: 0px;
    --row-inset-l: max(var(--gutter-inner), var(--safe-l));
    --row-inset-r: max(var(--gutter-inner), var(--safe-r));
    /* What a full-bleed row pays itself, so its text lands where the handover
       says. The mobile arm is in the media query below. */
    --row-pad-l: calc(var(--row-inset-l) + var(--space-3));
    --row-pad-r: calc(var(--row-inset-r) + var(--space-3));
    /* Total screen-edge-to-content distance, for a block that is content in its
       own right rather than a row inside a padded container — a results header,
       an empty-state notice. 24px desktop, 16px mobile, from whichever side of
       the handover is in force. Text needs its inset wherever it lives; only
       backgrounds and dividers benefit from bleeding. */
    --content-l: calc(var(--gutter-outer) + var(--row-inset-l));
    --content-r: calc(var(--gutter-outer) + var(--row-inset-r));

    --ring: 0 0 0 2px color-mix(in oklab, var(--accent) 45%, transparent);
    /* Press wash, for :active on anything tappable. Needed as a token because
       the browser's own tap flash is switched off app-wide (design/base.ts) and
       five components had to grow a press state at once; a per-component
       color-mix would have drifted the way the .acts cluster did.

       Ink over transparent rather than a --surface step: it composites over
       whatever ground the control already sits on (nothing, --surface, a
       thumbnail), so one value serves a ghost button and a photo tile. It flips
       with the theme for free, since --text does. Stated stronger than the
       --surface-2 hover on purpose: on touch a tap leaves a sticky :hover
       behind, and if press matched hover the press moment would be invisible
       under the state that outlives it. */
    --press: color-mix(in oklab, var(--text) 10%, transparent);
    --shadow-1: 0 1px 2px rgb(0 0 0 / 0.06);

    /* Place-kind hues. The one categorical palette in the system, and the only
       colours that carry meaning rather than emphasis: a pin, its list row, its
       chip and its card are tied together by hue alone, across four separate
       shadow roots. Custom properties inherit through those boundaries, which is
       what makes one declaration here enough.

       Each hue is stated twice — deep here for the light theme, bright in the
       dark block below. An earlier version used one mid-tone value for both, on
       the theory that a colour meaning "Food" should not move when the lights
       go out. Measured, that theory cost too much: mid-tone coral scored 2.8:1
       on a light chip and 2.0:1 on a pale basemap, so pins genuinely disappeared
       into the map. The hue *identity* is what has to hold, not the lightness —
       rust and coral both read as "the Food one", and each is legible where it
       is actually used. These land at 3.3–5.9:1 across the chip surface and real
       basemap colours (land, park green, water, road white).

       --k-off is for a venue we could not geocode: grey, because "no location"
       is an absence, not a fifth kind. */
    --k-food: #b8442a;
    --k-stay: #2b7a70;
    --k-place: #9c6410;
    --k-shop: #7a52a5;
    --k-off: #6b6357;
    --k-tint: 14%;
  }

  /* No density scale: v2 has no density control anywhere, so a [data-density]
     ramp would be dead code. v1 keeps its own until cutover. */

  :root[data-theme='dark'] {
    --bg: #17140f;
    --surface: #201c16;
    --surface-2: #2b251d;
    --text: #f0e9dc;
    --text-dim: #a99c88;
    --border: #3a3229;
    --accent: #e8a13d;
    --accent-ink: #1a1509;
    --danger: #e0715a;
    --shadow-1: 0 1px 2px rgb(0 0 0 / 0.4);
    /* The bright end of each kind hue — same identity as the light-theme set
       above, lifted to carry on a dark surface and a dark basemap. Measured
       5.7–7.7:1 against --surface. */
    --k-food: #e0785c;
    --k-stay: #63b0a7;
    --k-place: #e8a13d;
    --k-shop: #b899d4;
    --k-off: #8a8072;
    /* Tints need more presence against a dark surface to read as a fill rather
       than as noise. */
    --k-tint: 22%;
  }

  /* The gutter handover — the only layout token that moves at the breakpoint.
     BP_DESKTOP (960); media queries can't read a custom property, so the value
     is mirrored from the constant above. Keep them in sync.

     Width rather than pointer:coarse, per ADR 0007 principle 2: this is an
     arrangement question, and a narrow desktop window genuinely does want the
     full-bleed treatment. */
  @media (max-width: 959px) {
    :root {
      --gutter-outer: 0px;
      --gutter-inner: var(--space-4);
      /* No extra step: nothing pads the row from outside, so its own inset is
         the whole distance from the screen edge to its text. */
      --row-pad-l: var(--row-inset-l);
      --row-pad-r: var(--row-inset-r);
    }
  }
`;

const FONTS = css`
  @font-face {
    font-family: 'Bricolage';
    src: url('/static/fonts/bricolage.woff2') format('woff2');
    font-weight: 400 800;
    font-display: swap;
  }
  @font-face {
    font-family: 'Newsreader';
    src: url('/static/fonts/newsreader.woff2') format('woff2');
    font-style: italic;
    font-weight: 400 600;
    font-display: swap;
  }
  @font-face {
    font-family: 'Albert';
    src: url('/static/fonts/albert.woff2') format('woff2');
    font-weight: 400 700;
    font-display: swap;
  }
  @font-face {
    font-family: 'SplineMono';
    src: url('/static/fonts/splinemono.woff2') format('woff2');
    font-weight: 400 600;
    font-display: swap;
  }
`;

export const tokensCss = FONTS + TOKENS;
